import { type CreatePatientProfileRequest } from '@heidi/patients/contracts';
import { type EHRProvider, Shared__enums__enums__Gender } from '@heidi/schema/ml-scribe';

import {
  type Gdt35ExtendedContext,
  type GdtPatientData,
  getPatientDemographicString,
} from '../types';

/**
 * Convert GDT gender string to Shared__enums__enums__Gender enum value
 */
function convertGdtGenderToGenderEnum(
  gender: GdtPatientData['gender']
): Shared__enums__enums__Gender | undefined {
  switch (gender) {
    case 'male':
      return Shared__enums__enums__Gender.male;
    case 'female':
      return Shared__enums__enums__Gender.female;
    case 'other':
      return Shared__enums__enums__Gender.other;
    case 'unknown':
      return Shared__enums__enums__Gender.unknown;
    default:
      return undefined;
  }
}

/**
 * Format all GDT 3.5 extended context into a single additional_context string
 * All extended data (medications, allergies, diagnoses, labs, vitals, comments)
 * is combined into one field since CreatePatientProfileRequest only has additional_context
 */
function formatExtendedContextForProfile(ctx: Gdt35ExtendedContext): string | undefined {
  const parts: string[] = [];

  // Diagnoses
  if (ctx.diagnoses.length > 0) {
    const section = ['Diagnosen:'];
    ctx.diagnoses.forEach((dx) => {
      let line = '  ';
      if (dx.icd10Code) line += `[${dx.icd10Code}] `;
      if (dx.text) line += dx.text;
      if (dx.certainty) line += ` (${dx.certainty})`;
      if (line.trim()) section.push(line);
    });
    if (section.length > 1) parts.push(section.join('\n'));
  }

  // Medications
  if (ctx.medications.length > 0) {
    const section = ['Aktuelle Medikation:'];
    ctx.medications.forEach((med) => {
      let line = `  ${med.name}`;
      if (med.dosage) line += ` ${med.dosage}`;
      if (med.form) line += ` (${med.form})`;
      if (med.instructions) line += ` - ${med.instructions}`;
      section.push(line);
    });
    parts.push(section.join('\n'));
  }

  // Allergies
  if (ctx.allergies.length > 0) {
    const section = ['Allergien:'];
    ctx.allergies.forEach((allergy) => {
      let line = `  ${allergy.substance}`;
      if (allergy.allergyType) line = `  ${allergy.allergyType}: ${allergy.substance}`;
      if (allergy.severity) line += ` (${allergy.severity})`;
      section.push(line);
    });
    parts.push(section.join('\n'));
  }

  // Lab results
  if (ctx.labResults.length > 0) {
    const section = ['Laborwerte:'];
    ctx.labResults.forEach((lab) => {
      let line = `  ${lab.testName}: ${lab.result}`;
      if (lab.unit) line += ` ${lab.unit}`;
      if (lab.referenceRange) line += ` (Ref: ${lab.referenceRange})`;
      if (lab.status) line += ` [${lab.status}]`;
      section.push(line);
    });
    parts.push(section.join('\n'));
  }

  // Vital signs
  if (ctx.vitalSigns.length > 0) {
    const section = ['Vitalzeichen:'];
    ctx.vitalSigns.forEach((vital) => {
      let line = `  ${vital.vitalType}: ${vital.value}`;
      if (vital.unit) line += ` ${vital.unit}`;
      if (vital.date) line += ` (${vital.date})`;
      section.push(line);
    });
    parts.push(section.join('\n'));
  }

  // Comments
  if (ctx.comments.length > 0) {
    parts.push('Anmerkungen:\n  ' + ctx.comments.join('\n  '));
  }

  return parts.length > 0 ? parts.join('\n\n') : undefined;
}

/**
 * Convert GDT patient data to CreatePatientProfileRequest
 *
 * Maps GDT fields to Heidi's PatientProfile format:
 *
 * ## Basic Fields (GDT 2.1+)
 * - patientId -> ehr_patient_id (unique ID from PVS)
 * - firstName -> first_name
 * - lastName -> last_name
 * - dateOfBirth -> birth_date (already in ISO format)
 * - gender -> gender (enum conversion)
 *
 * ## GDT 3.5 Extended Fields
 * All extended context (medications, allergies, diagnoses, labs, vitals, comments)
 * is formatted into the additional_context field as human-readable German text.
 *
 * @param gdtPatient - Patient data from GDT file
 * @param ehrProvider - The EHR provider to tag on the patient profile
 * @returns Request object for creating a patient profile
 */
export function convertGdtToPatientProfile(
  gdtPatient: GdtPatientData,
  ehrProvider?: EHRProvider
): CreatePatientProfileRequest {
  const ctx = gdtPatient.extendedContext;

  return {
    // Map GDT patient ID as EHR patient ID for linking
    ehr_patient_id: gdtPatient.patientId,
    ehr_provider: ehrProvider,
    first_name: gdtPatient.firstName,
    last_name: gdtPatient.lastName,
    birth_date: gdtPatient.dateOfBirth ?? undefined,
    gender: convertGdtGenderToGenderEnum(gdtPatient.gender),
    // Generate demographic string from patient data
    demographic_string: getPatientDemographicString(gdtPatient),
    // GDT 3.5 extended context as formatted text
    additional_context: ctx ? formatExtendedContextForProfile(ctx) : undefined,
  };
}
